import {
  computed,
  effect,
  Injectable,
  Signal,
  signal,
  WritableSignal,
  inject,
} from "@angular/core";
import { PatientsApiService } from "../../api/users/patients-api.service";
import { Observable, of, tap } from "rxjs";
import {
  CreatePatientDto,
  ResponsePatientDto,
  UpdatePatientDto,
  FilterPatientDto,
} from "../../../api/models";
import { CachedServiceBase } from "../../base/cached-service.base";

@Injectable({
  providedIn: "root",
})
export class CachedPatientsService extends CachedServiceBase<ResponsePatientDto> {
  private readonly _patientsApiService = inject(PatientsApiService);
  // =============================================================================
  // DB FILTERS (trigger backend calls)
  // =============================================================================
  /** Doctor ID filter - triggers DB call when changed */
  private readonly _doctorId: WritableSignal<string | null> = signal(null);
  /** Psycho ID filter - triggers DB call when changed */
  private readonly _psychoId: WritableSignal<string | null> = signal(null);
  /** Clinic ID filter - triggers DB call when changed */
  private readonly _clinicId: WritableSignal<string | null> = signal(null);

  // =============================================================================
  // CLIENT-SIDE FILTERS (live filtering)
  // =============================================================================
  /** First name filter for live search */
  public readonly firstNameFilter: WritableSignal<string> = signal("");

  /** Last name filter for live search */
  public readonly lastNameFilter: WritableSignal<string> = signal("");

  /** Active pathway filter for live search */
  public readonly activePathwayFilter: WritableSignal<string> = signal("");

  // =============================================================================
  // DATA & STATE
  // =============================================================================

  // =============================================================================
  // CONSTRUCTOR & EFFECTS
  // =============================================================================
  constructor() {
    super();

    // Effect: reset pagination when filters change
    effect(() => {
      this.firstNameFilter();
      this.lastNameFilter();
      this.activePathwayFilter();
      // Reset to first page when client filters change
      this.resetPagination();
    });
  }

  // =============================================================================
  // IMPLEMENT ABSTRACT METHODS
  // =============================================================================

  /**
   * Apply client-side filters to backend data
   */
  protected getFilteredData(): Signal<ResponsePatientDto[]> {
    return computed(() => {
      const allData = this.data();
      const firstName = this.firstNameFilter().toLowerCase().trim();
      const lastName = this.lastNameFilter().toLowerCase().trim();
      const activePathway = this.activePathwayFilter().trim();

      if (!firstName && !lastName && !activePathway) return allData;

      return allData.filter(
        (item) =>
          (!firstName || item.firstName?.toLowerCase().includes(firstName)) &&
          (!lastName || item.lastName?.toLowerCase().includes(lastName)) &&
          (!activePathway || item.activePathway === activePathway),
      );
    });
  }

  public refresh(): void {
    const doctorId = this._doctorId();
    const psychoId = this._psychoId();
    const clinicId = this._clinicId();
    
    if (doctorId || psychoId || clinicId) {
      this._fetchFromBackend(doctorId, clinicId, psychoId);
    }
  }

  public reset(): void {
    this._resetFilters();
    this.clearTextFilters();
    this.clearData();
  }

  // =============================================================================
  // COMPUTED SIGNALS
  // =============================================================================

  /** Signal to track if there are active text filters */
  public readonly hasActiveFilters: Signal<boolean> = computed(() => {
    const firstName = this.firstNameFilter().trim();
    const lastName = this.lastNameFilter().trim();
    const activePathway = this.activePathwayFilter().trim();
    return !!(firstName || lastName || activePathway);
  });

  /** Filtered data based on client-side filters */
  public readonly filteredData: Signal<ResponsePatientDto[]> =
    this.getFilteredData();

  // =============================================================================
  // PRIVATE METHODS
  // =============================================================================

  /**
   * Fetch data from backend based on provided filters
   */
  private _fetchFromBackend(
    doctorId: string | null = null, 
    clinicId: string | null = null,
    psychoId: string | null = null
  ): void {
    const filterData: Partial<FilterPatientDto> = {
      ...(doctorId && { doctorId }),
      ...(psychoId && { psychoId }),
      ...(clinicId && { clinicId })
    };

    this._patientsApiService
      .filter$(filterData)
      .pipe(tap(results => this.updateData(results)))
      .subscribe();
  }

  // =============================================================================
  // PUBLIC API
  // =============================================================================

  /**
   * Set doctor ID and trigger backend call
   */
  public setDoctorId(doctorId: string): void {
    this._resetFilters();
    this._doctorId.set(doctorId);
    this._fetchFromBackend(doctorId);
  }

  /**
   * Set psycho ID and trigger backend call
   */
  public setPsychoId(psychoId: string): void {
    this._resetFilters();
    this._psychoId.set(psychoId);
    this._fetchFromBackend(null, null, psychoId);
  }

  /**
   * Set clinic ID and trigger backend call.
   */
  public setClinicId(clinicId: string): void {
    this._resetFilters();
    this._clinicId.set(clinicId);
    this._fetchFromBackend(null, clinicId);
  }

  /**
   * Reset all filter IDs to null
   */
  private _resetFilters(): void {
    this._doctorId.set(null);
    this._psychoId.set(null);
    this._clinicId.set(null);
  }

  /**
   * Clear text filters (firstName, lastName, activePathway)
   */
  public clearTextFilters(): void {
    this.firstNameFilter.set("");
    this.lastNameFilter.set("");
    this.activePathwayFilter.set("");
  }

  /**
   * Legacy compatibility - reset error (not needed with new architecture)
   */
  public resetError(): void {
    // No-op - error handling is centralized now
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public nextPage(): void {
    this.goToNextPage();
  }

  /**
   * Legacy compatibility - using base class pagination
   */
  public previousPage(): void {
    this.goToPreviousPage();
  }

  /**
   * Legacy compatibility - get current page index
   */
  public currentPageIndex(): number {
    return this.paginationInfo().currentPage;
  }

  /**
   * Legacy compatibility - has next page
   */
  public hasNextPage(): boolean {
    return this.paginationInfo().hasNextPage;
  }

  /**
   * Legacy compatibility - has previous page
   */
  public hasPreviousPage(): boolean {
    return this.paginationInfo().hasPreviousPage;
  }

  /**
   * Legacy compatibility - should show pagination
   */
  public shouldShowPagination(): boolean {
    return this.paginationInfo().shouldShowPagination;
  }

  /**
   * Legacy compatibility - get total pages
   */
  public totalPages(): number {
    return this.paginationInfo().totalPages;
  }

  // =============================================================================
  // CRUD OPERATIONS
  // =============================================================================

  /**
   * Creates a new patient
   * @param body The DTO containing patient creation data
   * @returns Observable of the created patient
   */
  public create$(body: CreatePatientDto): Observable<ResponsePatientDto> {
    return this._patientsApiService
      .create$(body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Updates an existing patient
   * @param id The ID of the patient
   * @param body The DTO containing patient update data
   * @returns Observable of the updated patient
   */
  public update$(
    id: string,
    body: UpdatePatientDto,
  ): Observable<ResponsePatientDto> {
    return this._patientsApiService
      .update$(id, body)
      .pipe(tap(() => this.refresh()));
  }

  /**
   * Deletes a patient by ID
   * @param id The ID of the patient
   * @returns Observable of operation result
   */
  public delete$(id: string): Observable<void> {
    return this._patientsApiService.delete$(id).pipe(tap(() => this.refresh()));
  }

  /**
   * Retrieves a single patient
   * @param id The ID of the patient to retrieve
   * @returns Observable of the patient
   */
  public findOne$(id: string): Observable<ResponsePatientDto> {
    // Search in backend data cache
    const allData = this.data();
    const cached = allData.find((p) => p.id === id);

    if (cached) {
      return of(cached);
    }

    return this._patientsApiService.findOne$(id);
  }

  // =============================================================================
  // LEGACY COMPATIBILITY FOR EXISTING COMPONENTS
  // =============================================================================

  /**
   * Legacy compatibility - get all patients (for backward compatibility)
   * @param forceRefresh Force refresh of cached data
   * @returns Observable of all patients
   */
  public getPatients$(
    forceRefresh: boolean = false,
  ): Observable<ResponsePatientDto[]> {
    if (forceRefresh) {
      this.refresh();
    }
    return of(this.filteredData());
  }
}
